import { InputField } from 'components/layout/form-fields/inputs/InputField';
import React from 'react';
import Dropdown from '../dropdown/Dropdown';
import Calendar from '../calendar/Calendar';
import { observer } from 'mobx-react';
import { UsersDropdown } from 'components/layout/form-fields/inputs/UsersDropdown';
import { TimezoneDropdown } from 'components/timezones/shared/TimezoneDropdown';
import { Switch } from 'components/layout/form-fields/inputs/Switch';
import CheckboxGroup from '../checkbox-group/CheckboxGroup';
import { CountryDropdown } from 'components/candidates/CountryDropdown';
import MultiDropdownGrouped from '../multi-dropdown-grouped/MultiDropdownGrouped';
import StageFilterDropdown from '../stage-filter-dropdown/StageFilterDropdown';
import { ClientContactsDropdown } from 'components/layout/form-fields/inputs/ClientContactsDropdown';
import { TagsDropdown } from 'components/candidates/TagsDropdown';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';
import { JobPositionDropdown } from 'components/layout/form-fields/inputs/JobPositionDropdown';
import { ClientsDropdown } from 'components/layout/form-fields/inputs/ClientsDropdown';
import { SkillDropdown } from 'components/layout/form-fields/inputs/SkillDropdown';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { CurrencyDropdown } from 'components/layout/form-fields/inputs/CurrencyDropdown';
import { ApplicantDropdown } from 'components/layout/form-fields/inputs/ApplicantDropdown';
import CreatableMultiSelect from '../creatable-multi-select/CreatableMultiSelect';

function normalizeAppliedFilter(filter) {
	if (typeof filter === 'string') {
		return { label: filter, onRemove: null };
	}

	return {
		label: filter?.label,
		onRemove: filter?.onRemove || null
	};
}

function FilterDrawer({ filters, getAppliedFilters, onReset }) {
	const filterComponents = {
		search: ({ key, label, name, value, onKeyPress, onChange }) => (
			<InputField
				key={key}
				autoFocus={true}
				onKeyPress={onKeyPress}
				className="has-text-left no-margin-bottom"
				field={name}
				type="search"
				icon="fa-search"
				placeholder={label}
				value={value}
				onValueChange={onChange}
				debounce
				debounceTime={400}
			/>
		),
		text: ({
			key,
			label,
			name,
			value,
			onKeyPress,
			onChange,
			placeholder = ' '
		}) => (
			<InputField
				key={key}
				onKeyPress={onKeyPress}
				className="has-text-left no-margin-bottom"
				field={name}
				type="text"
				value={value}
				defaultValue={value}
				placeholder={placeholder}
				onValueChange={onChange}
				debounce
				debounceTime={400}
			/>
		),
		number: ({
			key,
			label,
			name,
			value,
			onKeyPress,
			onChange,
			placeholder = ' '
		}) => (
			<InputField
				key={key}
				onKeyPress={onKeyPress}
				className="has-text-left no-margin-bottom"
				field={name}
				type="number"
				defaultValue={value}
				value={value}
				placeholder={placeholder}
				onValueChange={onChange}
				debounce
				debounceTime={400}
			/>
		),
		'creatable-multi': ({
			key,
			value,
			onChange,
			placeholder = ' ',
			isClearable,
			isValidNewOption,
			getNewOptionData
		}) => (
			<CreatableMultiSelect
				key={key}
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				isClearable={isClearable}
				isValidNewOption={isValidNewOption}
				getNewOptionData={getNewOptionData}
			/>
		),
		dropdown: ({
			key,
			name,
			onChange,
			value,
			options,
			placeholder = ' ',
			optionValue
		}) => (
			<Dropdown
				key={key}
				value={value}
				name={name}
				onChange={onChange}
				options={options}
				placeholder={placeholder}
				optionValue={optionValue}
			/>
		),
		'multi-dropdown': ({
			key,
			name,
			onChange,
			value,
			options,
			getOptionLabel,
			placeholder = ' '
		}) => (
			<MultiDropdownGrouped
				key={key}
				name={name}
				className="ats-multi-dropdown ats-multi-dropdown-grouped react-select"
				isClearable={true}
				placeholder={placeholder}
				options={options}
				getOptionLabel={getOptionLabel}
				onChange={e => onChange(e)}
				value={value}
				menuPlacement="auto"
				menuPosition="fixed"
			/>
		),
		'stage-filter-dropdown': ({
			key,
			name,
			onChange,
			value,
			options,
			placeholder = 'All',
			groups,
			stageToStatus,
			statusGroupMap,
			renderStage,
			renderValueIcon,
			getValueColor
		}) => (
			<StageFilterDropdown
				key={key}
				name={name}
				placeholder={placeholder}
				options={options}
				groups={groups}
				stageToStatus={stageToStatus}
				statusGroupMap={statusGroupMap}
				renderStage={renderStage}
				renderValueIcon={renderValueIcon}
				getValueColor={getValueColor}
				onChange={onChange}
				value={value}
			/>
		),
		calendar: ({ onChange, value, placeholder, isRange }) => (
			<Calendar
				value={value}
				placeholder={placeholder}
				isRange={isRange}
				onChange={onChange}
			/>
		),
		'user-dropdown': ({
			value,
			onChange,
			role,
			placeholder = ' ',
			isMulti,
			isClearable,
			minSearchChars
		}) => (
			<UsersDropdown
				value={value}
				isMulti={isMulti}
				placeholder={placeholder}
				role={role}
				minSearchChars={minSearchChars}
				onValueChange={onChange}
				isClearable={isClearable}
				showAvatar
			/>
		),
		'timezone-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			isMulti,
			isClearable
		}) => (
			<TimezoneDropdown
				isMulti={isMulti}
				value={value}
				placeholder={placeholder}
				onValueChange={onChange}
				isClearable={isClearable}
				usePurpleStyles
			/>
		),
		'country-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			isMulti,
			isClearable
		}) => (
			<CountryDropdown
				isMulti={isMulti}
				value={value}
				placeholder={placeholder}
				onValueChange={onChange}
				isClearable={isClearable}
			/>
		),
		switch: ({ id, value, onChange }) => (
			<Switch id={id} value={!value} onValueChange={onChange} />
		),
		'checkbox-group': ({
			options,
			value,
			onChange,
			label,
			colors,
			allowMultiple
		}) => (
			<CheckboxGroup
				options={options}
				value={value}
				onValueChange={onChange}
				label={label}
				colors={colors}
				allowMultiple={allowMultiple}
			/>
		),
		'client-contact-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			isMulti,
			isClearable
		}) => (
			<ClientContactsDropdown
				value={value}
				placeholder={placeholder}
				isMulti={isMulti}
				isClearable={isClearable}
				onValueChange={onChange}
			/>
		),
		'tags-dropdown': ({ value, onChange, placeholder = ' ', isClearable }) => (
			<TagsDropdown
				onValueChange={onChange}
				value={value}
				placeholder={placeholder}
				isClearable={isClearable}
			/>
		),
		'applicant-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			isClearable,
			isMulti
		}) => (
			<ApplicantDropdown
				onValueChange={onChange}
				value={value}
				placeholder={placeholder}
				isClearable={isClearable}
				showAvatar
				isMulti={isMulti}
			/>
		),
		'job-role-dropdown': ({ value, onChange, placeholder = ' ' }) => (
			<JobRoleDropdown
				isMulti
				includeLegacy
				value={value}
				placeholder={placeholder}
				styleVersion="ats"
				onValueChange={onChange}
			/>
		),
		'client-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			isClearable,
			isMulti
		}) => (
			<ClientsDropdown
				value={value}
				isMulti={isMulti}
				placeholder={placeholder}
				onValueChange={onChange}
				isClearable={isClearable}
			/>
		),
		'job-position-dropdown': ({
			value,
			onChange,
			placeholder = ' ',
			showOnlyOpenJobs
		}) => (
			<JobPositionDropdown
				value={value}
				placeholder={placeholder}
				styleVersion="ats"
				showOnlyOpenJobs={showOnlyOpenJobs}
				onValueChange={onChange}
			/>
		),
		'skill-dropdown': ({ value, onChange, placeholder = ' ' }) => (
			<SkillDropdown
				isMulti
				value={value}
				placeholder={placeholder}
				styleVersion="ats"
				onValueChange={onChange}
			/>
		),
		compensation: ({ value = {}, onChange }) => {
			return (
				<>
					<div className="compensation-filters-container">
						<div className="compensation-filter-input">
							<label>Currency</label>
							<CurrencyDropdown
								value={value?.currency}
								placeholder=" "
								styleVersion="ats"
								isClearable
								onValueChange={item => onChange({ ...value, currency: item })}
							/>
						</div>
						<div className="compensation-filter-input">
							<label>Scheme</label>
							<Dropdown
								value={value?.scheme}
								placeholder=" "
								onChange={item => onChange({ ...value, scheme: item })}
								options={[
									{ label: 'Any', value: null },
									...applicantStore.compensationsTypesOptions
								]}
								optionValue="value"
							/>
						</div>
					</div>
					<div className="compensation-filters-container">
						<div className="compensation-filter-input">
							<label>Salary Range</label>
							<div className="is-flex gap-2">
								<InputField
									className="has-text-left no-margin-bottom"
									field="amount-range-min"
									type="number"
									defaultValue={value?.amountRange?.[0] || ''}
									value={value?.amountRange?.[0] || ''}
									placeholder=" "
									min={0}
									onValueChange={item =>
										onChange({
											...value,
											amountRange: [item, value?.amountRange?.[1]]
										})
									}
								/>
								<InputField
									className="has-text-left no-margin-bottom"
									field="amount-range-max"
									type="number"
									defaultValue={value?.amountRange?.[1] || ''}
									value={value?.amountRange?.[1] || ''}
									placeholder=" "
									onValueChange={item =>
										onChange({
											...value,
											amountRange: [value?.amountRange?.[0], item]
										})
									}
								/>
							</div>
						</div>
					</div>
				</>
			);
		}
	};

	const filtersEntities = Array.from(
		new Set(filters?.map(f => f.entity).filter(Boolean))
	);

	const appliedFilters = getAppliedFilters().map(normalizeAppliedFilter);

	return (
		<div className="filter-drawer-container">
			{appliedFilters.length > 0 && (
				<div className="applied-filters-summary">
					<div className="applied-filters-container">
						{appliedFilters.map(filter => (
							<span className="applied-filter-item" key={filter.label}>
								<span className="applied-filter-item__label">
									{filter.label}
								</span>
								{filter.onRemove ? (
									<button
										type="button"
										className="applied-filter-item__remove"
										aria-label={`Remove ${filter.label} filter`}
										onClick={event => {
											event.preventDefault();
											event.stopPropagation();
											filter.onRemove();
										}}>
										<img
											src="/ats/icons/clear-select-icon.svg"
											alt=""
											width={14}
											height={14}
										/>
									</button>
								) : null}
							</span>
						))}
					</div>
					<button className="filter-drawer-reset-btn" onClick={onReset}>
						Reset All
					</button>
				</div>
			)}
			{filtersEntities.length > 0 ? (
				filtersEntities.map(entity => {
					const entityFilters = filters.filter(f => f.entity === entity);

					return (
						<div className="filters-container">
							<h4>{entity} Filters</h4>
							{entityFilters?.map((f, index) => (
								<div className="filter-wrapper" key={index}>
									{f.label ? <label>{f.label}</label> : null}
									{filterComponents[f.type]({
										...f,
										key: index
									})}
								</div>
							))}
						</div>
					);
				})
			) : (
				<div className="filters-container">
					{filters?.map((f, index) => (
						<div className="filter-wrapper" key={index}>
							{f.label ? <label>{f.label}</label> : null}
							{filterComponents[f.type]({
								...f,
								key: index
							})}
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export default observer(FilterDrawer);
